import TrieSearch from 'trie-search';
import { handleException } from '~/modules/utilities/error_utilities';
import { clearSearchSuggestionCache, getSearchSuggestionData } from './searchSuggestionCacheAdapter';
export interface TrieSearchItem {
    keyword: string;
}
class TrieService {
    private trie: TrieSearch<TrieSearchItem>;
    public isInitialized = false;
    private initializationPromise?: Promise<void>;

    constructor() {
        this.trie = new TrieSearch<TrieSearchItem>('keyword', {
            ignoreCase: true,
            splitOnRegEx: /\s+/,
            cache: true, // Enable caching for faster repeated searches
            maxCacheSize: 100,
        });
        this.initialize();
    }

    initialize(): Promise<void> {
        if (this.isInitialized) {
            return Promise.resolve();
        }

        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        this.initializationPromise = (async () => {
            try {
                const { data: initialSearchData } = await getSearchSuggestionData();
                if (initialSearchData.length > 0) {
                    this.trie.addAll(initialSearchData);
                }
                this.isInitialized = true;
            } catch (error) {
                handleException(error, 'trieService.initialize');
            } finally {
                this.initializationPromise = undefined;
            }
        })();

        return this.initializationPromise;
    }

    search(query: string, limit = 10): TrieSearchItem[] {
        if (!query) {
            return [];
        }

        const trimmedQuery = query.trim();
        const queryLower = trimmedQuery.toLowerCase();
        const results = this.trie.search(trimmedQuery);

        results.sort((a, b) => {
            const aLower = a.keyword.toLowerCase();
            const bLower = b.keyword.toLowerCase();
            const aExact = aLower === queryLower;
            const bExact = bLower === queryLower;
            const aPrefix = aLower.includes(queryLower);
            const bPrefix = bLower.includes(queryLower);

            if (aExact !== bExact) {
                return aExact ? -1 : 1;
            }
            if (aPrefix !== bPrefix) {
                return aPrefix ? -1 : 1;
            }
            return aLower.localeCompare(bLower);
        });

        return results.slice(0, limit);
    }

    addItems(items: TrieSearchItem[]): void {
        this.trie.addAll(items);
    }

    addItem(item: TrieSearchItem): void {
        this.trie.add(item);
    }

    clearCache(): void {
        this.trie.clearCache();
    }

    reset(): void {
        if (!this.isInitialized || !this.trie) {
            return;
        }
        this.trie.reset();
        this.isInitialized = false;
        clearSearchSuggestionCache();
        this.initialize();
    }

    get initialized(): boolean {
        return this.isInitialized;
    }
}

export { TrieService };
